import moment from 'moment-timezone';

// Pure, network-free logic extracted from payment-method.js so it is unit-testable
// without booting the PSP-SDK-poisoned component (infinite tickTask + external SDKs).
// See CLAUDE.md / FOUNDATION-CHROME-INTERACTION-PLAN.md §11.4: extract-or-ignore.

/**
 * PayPal only accepts a whitelisted set of locale codes; anything else falls back
 * to it_IT. Input is a BCP-47-ish string like "it-IT" or "en-us".
 */
function payPalLocale(localeStr) {
  const [lingua, paese] = (localeStr || '').split('-');
  const linguaPaese = `${(lingua || '').toLowerCase()}_${(paese || '').toUpperCase()}`;
  const lingueAccettate = ['it_IT', 'en_US', 'fr_FR'];
  return lingueAccettate.includes(linguaPaese) ? linguaPaese : 'it_IT';
}

/**
 * SumUp wants lang-REGION (e.g. it-IT); normalizes case, passes through unchanged
 * when there's no region part.
 */
function formatLanguageCode(languageCode) {
  const parts = (languageCode || '').split('-');
  if (parts.length === 2) {
    const [language, region] = parts;
    return `${language.toLowerCase()}-${(region || '').toUpperCase()}`;
  } else {
    return languageCode;
  }
}

/**
 * Pure countdown/timeout calculation. Given the payment date (or a previously
 * seeded startDate), "now", the configured timeout in seconds, and whether we're
 * in a booking/ecommerce flow, returns whether the window is over plus (when
 * relevant) an m:ss countdown string. No side effects, no `this`.
 */
function computeTimeState({
  date,
  now,
  maxTime = 300,
  isTimedFlow
}) {
  let resolvedDate = date;
  let seededDate = null;
  if (!resolvedDate) {
    // Keep seconds: truncating to the minute made dateDiff jump up to 59s
    // ahead, shortening the payment window by almost a minute
    seededDate = now.clone().format('YYYY-MM-DDTHH:mm:ssZ');
    resolvedDate = seededDate;
  }
  const dateDiff = now.diff(resolvedDate, 'seconds');
  if (dateDiff >= maxTime && isTimedFlow) {
    return {
      timeOver: true,
      tempoRestante: null,
      seededDate
    };
  }
  let tempoRestante = null;
  if (isTimedFlow) {
    // deadline = resolvedDate + maxTime seconds
    const deadlineMoment = moment(resolvedDate).add(maxTime, 'seconds');
    const totalSecs = Math.max(0, deadlineMoment.diff(now, 'seconds'));
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs - 60 * mins;
    tempoRestante = `${mins}:${secs.toString().padStart(2, '0')}`;
  }
  return {
    timeOver: false,
    tempoRestante,
    seededDate
  };
}

/**
 * dati_banca arrives as a JSON string on /get_pos_method rows. A row saved
 * without bank data (undefined) or with malformed JSON used to throw
 * SyntaxError inside the row loop, aborting getIntegrationTask and hiding
 * EVERY payment method behind the load-error box. Parse defensively instead.
 */
function parseDatiBanca(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Given the raw /get_pos_method rows, the transaction total, and the fromBooking
 * flag, filters + classifies enabled payment services and decides which one gets
 * auto-selected first. Mirrors the switch/filter previously inlined inside the
 * getIntegrationTask generator (still there, now delegating here) so it can be
 * unit tested without a live XHR or the ember-concurrency scheduler.
 */
function buildPaymentEnable({
  rows,
  total,
  fromBooking,
  initialPaymentEnable
}) {
  const payment_enable = {
    ...initialPaymentEnable
  };
  let selectedService = null;
  if (!Array.isArray(rows)) {
    return {
      payment_enable,
      selectedService,
      methodExist: false
    };
  }
  for (const item of rows) {
    const {
      service,
      max_accettato,
      min_accettato,
      no_booking,
      no_manual,
      paypal_mail,
      merchant_id,
      dati_banca,
      user_id
    } = item;

    // Inclusive bounds: a total exactly equal to the configured min/max
    // accepted amount is still accepted
    const withinRange = (!max_accettato || total <= parseFloat(max_accettato)) && (!min_accettato || total >= parseFloat(min_accettato));
    const flowAllowed = fromBooking && !no_booking || !fromBooking && !no_manual;
    if (!(withinRange && flowAllowed)) {
      continue;
    }
    switch (service) {
      case 'paypal':
        payment_enable.paypal = {
          ...payment_enable.paypal,
          enable: true,
          data: item,
          mail: paypal_mail,
          merchant_id
        };
        break;
      case 'sumup':
        payment_enable.sumup = {
          ...payment_enable.sumup,
          enable: true,
          data: item
        };
        break;
      case 'satispay':
        payment_enable.satispay = {
          ...payment_enable.satispay,
          enable: true,
          data: item
        };
        break;
      case 'stripe':
        payment_enable.stripe = {
          ...payment_enable.stripe,
          enable: true,
          data: item
        };
        break;
      case 'pos_virtuale':
        payment_enable.pos_virtuale = {
          ...payment_enable.pos_virtuale,
          enable: true,
          data: item
        };
        break;
      case 'bonifico':
        {
          // A bank-transfer method without bank data is unusable: the template
          // would show an empty IBAN box and setBonificoTask would crash on
          // dati_banca.nome. Skip the row instead of enabling a broken method.
          const datiBancaBonifico = parseDatiBanca(dati_banca);
          if (!datiBancaBonifico) continue;
          payment_enable.bonifico = {
            ...payment_enable.bonifico,
            enable: true,
            dati_banca: datiBancaBonifico
          };
          break;
        }
      case 'stripe_bonifico':
        // Null dati_banca is legitimate here: setStripeBonificoTask fills it
        // from the /stripe/bonifico response. Only malformed JSON is coerced
        // to null instead of throwing.
        payment_enable.stripe_bonifico = {
          ...payment_enable.stripe_bonifico,
          enable: true,
          user_id,
          dati_banca: parseDatiBanca(dati_banca)
        };
        break;
      case 'in_loco':
        payment_enable.in_loco = {
          ...payment_enable.in_loco,
          enable: true
        };
        break;
      default:
        // Unknown/unsupported service: skip so it never becomes selectedService
        // (would auto-select a method that no case ever enabled -> stuck UI).
        continue;
    }
    if (!selectedService) {
      selectedService = service;
    }
  }
  const methodExist = Object.values(payment_enable).some(p => p.enable);
  return {
    payment_enable,
    selectedService,
    methodExist
  };
}

/**
 * Builds the payload sent to the external success-booking PHP endpoint.
 */
function buildSuccessBookingData({
  session,
  args,
  no_redirect = false
}) {
  return {
    contact_id: session.me?.id,
    organization: session.openOrganization?.organization,
    access_token: session.token,
    payment_id: args.payment.id,
    specialista_mail: args.contactSelected?.mail,
    specialista_name: args.contactSelected?.name,
    product_name: args.productSelected?.name,
    stanza: args.hourSelected?.stanza,
    no_redirect
  };
}

/**
 * Builds the pos-virtuale redirect URL (external PHP endpoint, no fetch).
 */
function buildPosVirtualeUrl({
  organization,
  contactId,
  paymentId,
  token,
  amount
}) {
  // Encode every interpolated value: raw token/org/id could inject or truncate
  // query params and misroute payment fields to the external endpoint.
  const org = encodeURIComponent(organization || '');
  const q = new URLSearchParams({
    contact_id: contactId || '',
    payment_id: paymentId ?? '',
    access_token: token ?? '',
    amount: amount ?? ''
  });
  return `https://asset.beebeeboard.com/${org}/transazione/ecommerce_init.php?${q}`;
}

export { buildPaymentEnable, buildPosVirtualeUrl, buildSuccessBookingData, computeTimeState, formatLanguageCode, payPalLocale };
//# sourceMappingURL=payment-logic.js.map
